import { useCallback, useEffect, useState } from "react";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { useOpenById } from "../lib/useOpenById";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const TYPES = ["price", "discount", "payment_terms", "freight"];
const TYPE_COLORS = { price: "bg-amber-100 text-amber-800", discount: "bg-blue-100 text-blue-700",
  payment_terms: "bg-indigo-100 text-indigo-700", freight: "bg-gray-100 text-gray-600" };
const EMPTY = { account_id: "", article_id: "", condition_type: "price", price: "", discount_pct: "",
  payment_terms: "", min_qty: "", currency: "EUR", valid_from: "", valid_to: "", notes: "" };

export default function Conditions() {
  const { t, canWrite } = useApp();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [accounts, setAccounts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [ctype, setCtype] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page, page_size: 50 });
    if (accountId) params.set("account_id", accountId);
    if (ctype) params.set("condition_type", ctype);
    api.get(`/conditions?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [page, accountId, ctype]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);
  useOpenById("conditions", setEditing);

  useEffect(() => {
    if (editing?.account_id) {
      api.get(`/articles?account_id=${editing.account_id}&page_size=200`).then((r) => setArticles(r.data.items)).catch(() => {});
    } else setArticles([]);
  }, [editing?.account_id]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/conditions/${editing.id}`, editing);
      else await api.post("/conditions", editing);
      toast.success(t("save") + " ✓");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/conditions/${editing.id}`);
      toast.success(t("delete") + " ✓");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const typeLabel = (ty) => t(`cond_type_${ty === "payment_terms" ? "payment" : ty}`);

  return (
    <div className="space-y-4" data-testid="conditions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("order_conditions")}</h1>
        {canWrite && (
          <Button data-testid="conditions-new-btn" onClick={() => setEditing({ ...EMPTY })}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("cond_new")}
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
          <select data-testid="conditions-filter-account" className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 max-w-[220px]"
            value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(1); }}>
            <option value="">{`${t("ord_customer")}: ${t("all")}`}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select data-testid="conditions-filter-type" className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700"
            value={ctype} onChange={(e) => { setCtype(e.target.value); setPage(1); }}>
            <option value="">{`${t("cond_type")}: ${t("all")}`}</option>
            {TYPES.map((ty) => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
          </select>
          <span className="text-xs text-gray-500 font-mono">{data.total} {t("records")}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-2.5 font-semibold">{t("ord_customer")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("cond_type")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("cond_article")}</th>
                <th className="px-4 py-2.5 font-semibold text-right">{t("cond_value")}</th>
                <th className="px-4 py-2.5 font-semibold text-right">{t("cond_min_qty")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("cond_valid_from")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("cond_valid_to")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} data-testid="conditions-row" onClick={() => canWrite && setEditing(c)}
                  className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors duration-150">
                  <td className="px-4 py-2.5 font-medium">{c.account_name}</td>
                  <td className="px-4 py-2.5"><Badge className={`${TYPE_COLORS[c.condition_type] || ""} hover:bg-inherit`}>{typeLabel(c.condition_type)}</Badge></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.article_number ? `${c.article_number} – ${c.article_name || ""}` : t("cond_all_articles")}</td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {c.condition_type === "price" && fmtEur(c.price)}
                    {c.condition_type === "discount" && `${c.discount_pct}%`}
                    {c.condition_type === "payment_terms" && c.payment_terms}
                    {c.condition_type === "freight" && (c.notes || "–")}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{c.min_qty ? Number(c.min_qty).toLocaleString("de-DE") : "–"}</td>
                  <td className="px-4 py-2.5">{fmtDate(c.valid_from)}</td>
                  <td className="px-4 py-2.5">{fmtDate(c.valid_to)}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">{t("empty_title")}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
          <div className="flex-1" />
          <span className="text-xs text-gray-500">{t("ord_page")} {data.page || page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t("edit") : t("cond_new")}</DialogTitle>
            <DialogDescription>{t("order_conditions")}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("ord_customer")} *</Label>
                  <select data-testid="condition-account-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                    value={editing.account_id || ""} onChange={(e) => set("account_id", e.target.value)}>
                    <option value="">–</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">{t("cond_type")}</Label>
                  <select data-testid="condition-type-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                    value={editing.condition_type} onChange={(e) => set("condition_type", e.target.value)}>
                    {TYPES.map((ty) => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("cond_article")}</Label>
                  <select data-testid="condition-article-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                    value={editing.article_id || ""} onChange={(e) => set("article_id", e.target.value)}>
                    <option value="">{t("cond_all_articles")}</option>
                    {articles.map((a) => <option key={a.id} value={a.id}>{`${a.article_number} – ${a.name}`}</option>)}
                  </select>
                </div>
                {editing.condition_type === "price" && (
                  <div>
                    <Label className="text-xs">{t("cond_price")} (€)</Label>
                    <Input type="number" step="0.01" data-testid="condition-price-input" className="mt-1" value={editing.price || ""} onChange={(e) => set("price", e.target.value)} />
                  </div>
                )}
                {editing.condition_type === "discount" && (
                  <div>
                    <Label className="text-xs">{t("cond_discount")}</Label>
                    <Input type="number" step="0.5" className="mt-1" value={editing.discount_pct || ""} onChange={(e) => set("discount_pct", e.target.value)} />
                  </div>
                )}
                {editing.condition_type === "payment_terms" && (
                  <div>
                    <Label className="text-xs">{t("cond_payment")}</Label>
                    <Input className="mt-1" placeholder="z. B. Net 45" value={editing.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} />
                  </div>
                )}
                <div>
                  <Label className="text-xs">{t("cond_min_qty")}</Label>
                  <Input type="number" className="mt-1" value={editing.min_qty || ""} onChange={(e) => set("min_qty", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">{t("cond_valid_from")}</Label>
                  <Input type="date" className="mt-1" value={editing.valid_from || ""} onChange={(e) => set("valid_from", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">{t("cond_valid_to")}</Label>
                  <Input type="date" className="mt-1" value={editing.valid_to || ""} onChange={(e) => set("valid_to", e.target.value)} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("notes")}</Label>
                  <Input className="mt-1" value={editing.notes || ""} onChange={(e) => set("notes", e.target.value)} />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                {editing.id ? (
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove} data-testid="condition-delete-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{t("delete")}
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditing(null)}>{t("cancel")}</Button>
                  <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={save} data-testid="condition-save-btn">{t("save")}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
